// CS-2075 — Request Rx for one known patient (Patient List row / Patient Details action).
// Reuses the Rx Request flow: RxProductSelectionPopup (practice locked) -> PrescriptionModalPopup (patient preselected).
import { useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { RxOpenAccessProduct, submitPrescription } from "../../../redux/slice/prescription/prescriptionSlice";
import { MarketProduct } from "../../../redux/slice/exploreMarket/exploreMarketSlice";
import RxProductSelectionPopup from "./RxProductSelectionPopup";
import PrescriptionModalPopup from "./PrescriptionModalPopup";

export type RequestRxPatient = { patientId: number; practiceId: number };

interface PatientRequestRxProps {
  /** Open the flow for this patient; null keeps it closed. */
  patient: RequestRxPatient | null;
  onClose: () => void;
}

// Same RxOpenAccessProduct -> MarketProduct mapping as PrescriptionList.handleRxProductSelect.
const toMarketProduct = (product: RxOpenAccessProduct): MarketProduct => ({
  productId: product.productId,
  productName: product.productName,
  hcpcsCode: product.hcpcsCode || null,
  productCategory: product.productCategory || null,
  composition: product.composition || null,
  allowableRate: null,
  listPrice: null,
  mues: null,
  manufacturerId: product.manufacturerId ?? null,
  manufacturerName: product.manufacturerName || null,
  isAgreementRequired: null,
  agreementStatus: null,
  agreementMode: null,
  productLogoId: null,
  isFavorited: false,
  productStatus: null,
  extendedPrice:
    product.extendedPrice != null && product.extendedPrice !== ""
      ? Number(product.extendedPrice)
      : null,
});

const PatientRequestRx: React.FC<PatientRequestRxProps> = ({ patient, onClose }) => {
  const dispatch = useAppDispatch();
  const [rxProduct, setRxProduct] = useState<MarketProduct | null>(null);

  const handleClose = () => {
    setRxProduct(null);
    onClose();
  };

  // Same dispatch / row extraction / error toast as PrescriptionList.handleSubmitPrescription.
  const handleSubmitPrescription = async (payload: any): Promise<any[] | null> => {
    if (!payload) return null;
    const result = await dispatch(submitPrescription({ request: payload, skipLoader: false }));
    const body = result?.payload;
    const rows = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : null;
    if (!rows?.length) {
      dispatch(setSingleAlertObj({
        message: body?.message || "An error occurred while submitting the Rx Request.",
        alertType: "error",
      }));
    }
    return rows;
  };

  if (!patient) return null;

  return rxProduct ? (
    <PrescriptionModalPopup
      product={rxProduct}
      isOpen
      initialPracticeId={patient.practiceId}
      initialPatientId={patient.patientId}
      lockPracticeSelection
      onClose={handleClose}
      onSubmit={handleSubmitPrescription}
    />
  ) : (
    <RxProductSelectionPopup
      open
      lockedPracticeId={patient.practiceId}
      onClose={handleClose}
      onProductSelect={(product) => setRxProduct(toMarketProduct(product))}
    />
  );
};

export default PatientRequestRx;
